import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";

const SECTIONS = [
  {
    n: "01",
    title: "Who we are",
    body: (
      <>
        <p>
          ResumeLab (&ldquo;we&rdquo;, &ldquo;us&rdquo;) provides AI-powered resume evaluation and
          rewriting. We're the data controller for the personal data described in this policy.
          Contact us at{" "}
          <a href="mailto:hello@resumelab.co.ke" className="text-[#002FA7] underline-offset-4 hover:underline">
            hello@resumelab.co.ke
          </a>.
        </p>
      </>
    ),
  },
  {
    n: "02",
    title: "Data we collect",
    body: (
      <>
        <p>We collect only what we need to run the Service:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <b>Account data</b> — email, name, profile picture (from Google OAuth if you sign in).
          </li>
          <li>
            <b>Resume content</b> — the text, PDF, DOCX, or LinkedIn copy you submit for analysis.
          </li>
          <li>
            <b>Audit data</b> — scores, AI rewrites, cover letters, evaluation timestamps.
          </li>
          <li>
            <b>Subscription data</b> — Stripe customer ID, subscription status, billing-period
            dates. We never see or store full card numbers — Stripe handles that.
          </li>
          <li>
            <b>Marketing list data</b> — email + source tag if you opt in via the launch list or
            exit-intent form.
          </li>
          <li>
            <b>Technical data</b> — IP address (used for the free-tier rate limit), user-agent,
            timestamps of requests. Not used for tracking ads.
          </li>
        </ul>
        <p>We do <b>NOT</b> use cookies for advertising or third-party tracking.</p>
      </>
    ),
  },
  {
    n: "03",
    title: "Why we use it (legal basis)",
    body: (
      <>
        <ul className="list-disc pl-6 space-y-1">
          <li><b>Contract</b> — to run the audits and subscriptions you signed up for.</li>
          <li><b>Legitimate interest</b> — to keep the Service secure, prevent abuse, and improve features.</li>
          <li><b>Consent</b> — for the launch-list / marketing emails (you can unsubscribe anytime).</li>
          <li><b>Legal obligation</b> — to comply with tax, accounting, and regulatory requirements.</li>
        </ul>
      </>
    ),
  },
  {
    n: "04",
    title: "Who we share data with (sub-processors)",
    body: (
      <>
        <p>We share data only with trusted vendors needed to run the Service:</p>
        <div className="border border-zinc-200">
          <div className="grid grid-cols-3 bg-zinc-50 border-b-2 border-zinc-900">
            <div className="p-3 overline">Vendor</div>
            <div className="p-3 overline">Purpose</div>
            <div className="p-3 overline">Location</div>
          </div>
          {[
            ["Stripe, Inc.", "Payments + subscriptions", "USA / EU"],
            ["Resend", "Transactional email", "EU (eu-west-1)"],
            ["OpenAI / Emergent", "LLM resume scoring + rewrites", "USA"],
            ["Google", "OAuth authentication", "USA"],
            ["MongoDB Atlas / Emergent hosting", "App database + hosting", "EU"],
          ].map(([v, p, l]) => (
            <div key={v} className="grid grid-cols-3 border-b border-zinc-200/70 text-sm">
              <div className="p-3 font-medium">{v}</div>
              <div className="p-3 text-zinc-600">{p}</div>
              <div className="p-3 text-zinc-600 font-mono text-xs">{l}</div>
            </div>
          ))}
        </div>
        <p className="text-sm text-zinc-500 mt-3">
          We sign data-processing agreements with these vendors and never sell your data.
        </p>
      </>
    ),
  },
  {
    n: "05",
    title: "How long we keep data",
    body: (
      <>
        <ul className="list-disc pl-6 space-y-1">
          <li><b>Account + audits</b> — until you delete your account.</li>
          <li><b>Anonymous IP-trial data</b> — 30 days, then auto-pruned.</li>
          <li><b>Subscriber email list</b> — until you unsubscribe.</li>
          <li><b>Billing records</b> — 7 years (tax law).</li>
          <li><b>Server logs</b> — 30 days.</li>
        </ul>
      </>
    ),
  },
  {
    n: "06",
    title: "Your rights (GDPR, CCPA & friends)",
    body: (
      <>
        <p>You can ask us at any time to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><b>Access</b> a copy of your personal data</li>
          <li><b>Correct</b> inaccurate or incomplete data</li>
          <li><b>Delete</b> your account and all associated data (right to erasure)</li>
          <li><b>Export</b> your audits in a portable format (data portability)</li>
          <li><b>Object</b> to certain processing or <b>withdraw consent</b> for marketing</li>
          <li><b>Lodge a complaint</b> with a supervisory authority (e.g. ODPC Kenya, ICO UK)</li>
        </ul>
        <p>
          Email{" "}
          <a href="mailto:hello@resumelab.co.ke" className="text-[#002FA7] underline-offset-4 hover:underline">
            hello@resumelab.co.ke
          </a>{" "}
          and we'll respond within 30 days.
        </p>
      </>
    ),
  },
  {
    n: "07",
    title: "Security",
    body: (
      <>
        <p>
          We use HTTPS for all traffic, encrypted storage at the hosting layer, OAuth-based
          authentication (no app-managed passwords), and isolated payment handling via Stripe.
          Despite this, no system is 100% secure — if we ever detect a breach affecting your data
          we will notify you within 72 hours.
        </p>
      </>
    ),
  },
  {
    n: "08",
    title: "International transfers",
    body: (
      <>
        <p>
          Some of our sub-processors are located in the United States. Where we transfer EU/UK
          personal data outside the EEA, we rely on the European Commission's Standard Contractual
          Clauses (SCCs) or adequacy decisions.
        </p>
      </>
    ),
  },
  {
    n: "09",
    title: "Children",
    body: (
      <>
        <p>
          ResumeLab is intended for users aged 16 and older. We don't knowingly collect data from
          children under 16. If you believe we have, contact us and we'll delete it.
        </p>
      </>
    ),
  },
  {
    n: "10",
    title: "Changes to this policy",
    body: (
      <>
        <p>
          We may update this policy. We'll post the latest version at{" "}
          <Link to="/privacy" className="text-[#002FA7] underline-offset-4 hover:underline">/privacy</Link>{" "}
          and update the &ldquo;Last updated&rdquo; date at the top. Material changes will also be
          announced via email if you have an account.
        </p>
      </>
    ),
  },
  {
    n: "11",
    title: "Contact",
    body: (
      <>
        <p>
          Privacy questions:{" "}
          <a href="mailto:hello@resumelab.co.ke" className="text-[#002FA7] underline-offset-4 hover:underline">
            hello@resumelab.co.ke
          </a>.
        </p>
      </>
    ),
  },
];

export default function Privacy() {
  return (
    <main className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-blue-50 via-fuchsia-50 to-amber-50 text-zinc-900" data-testid="privacy-page">
      <SEO
        title="Privacy Policy"
        description="ResumeLab Privacy Policy — GDPR-aligned, no data selling, full delete-on-request. Stripe + Resend sub-processors."
        path="/privacy"
      />
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-8 lg:py-14">
        <div className="overline mb-4">◆ LEGAL · PRIVACY</div>
        <h1 className="font-display font-extrabold text-5xl lg:text-6xl tracking-tight leading-[0.95] mb-6">
          Privacy<br />Policy.
        </h1>
        <p className="text-zinc-500 mb-2">Last updated: <span className="font-mono">May 22, 2026</span></p>
        <p className="text-zinc-500 mb-12">
          The short version: we collect what we need to score your resume, we don't sell your
          data, and you can delete your account at any time.
        </p>

        <div className="bg-zinc-50 border border-zinc-200/70 p-6 lg:p-8 mb-12">
          <div className="overline mb-3">◇ THE SHORT VERSION</div>
          <ul className="space-y-2 text-sm text-zinc-700">
            <li>• We collect: your email (if you sign in), the resume text you submit, and your audit history.</li>
            <li>• We don't sell your data. Ever.</li>
            <li>• Payments go through Stripe — we never see your card number.</li>
            <li>• Email{" "}
              <a href="mailto:hello@resumelab.co.ke" className="text-[#002FA7] underline-offset-4 hover:underline">
                hello@resumelab.co.ke
              </a>{" "}
              to delete your account and we'll wipe everything within 30 days.
            </li>
          </ul>
        </div>

        <div className="space-y-10">
          {SECTIONS.map((s) => (
            <section key={s.n} data-testid={`privacy-section-${s.n}`}>
              <div className="font-mono text-xs text-zinc-400 mb-2">{s.n} / {String(SECTIONS.length).padStart(2, "0")}</div>
              <h2 className="font-display font-extrabold text-2xl mb-4">{s.title}</h2>
              <div className="text-zinc-700 leading-relaxed space-y-3 text-[15px]">{s.body}</div>
            </section>
          ))}
        </div>

        <div className="mt-16 pt-8 border-t border-zinc-200 text-sm text-zinc-500">
          See also: <Link to="/terms" className="text-[#002FA7] underline-offset-4 hover:underline">Terms of Service</Link>.
        </div>
      </div>
      <Footer />
    </main>
  );
}
